import { ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../utils'
import { IconButton } from './Button'
import styles from './Modal.module.css'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  /** Rendered in the footer (typically action buttons) */
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /** Disable closing on backdrop click / Escape */
  dismissable?: boolean
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  dismissable = true,
}: ModalProps) {
  useEffect(() => {
    if (!open || !dismissable) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, dismissable, onClose])

  if (!open) return null

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={dismissable ? onClose : undefined}
      role="presentation"
    >
      <div
        className={cn(styles.modal, size === 'sm' && styles.sm, size === 'lg' && styles.lg)}
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        {title !== undefined && (
          <div className={styles.header}>
            <h3 className={styles.title}>{title}</h3>
            <IconButton variant="secondary" icon="bi bi-x-lg" aria-label="Close" onClick={onClose} />
          </div>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
